import React, { useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Slim Chat panel for use inside the Side-by-Side layout. Streams the same
 * /api/chat endpoint as ChatPage. State is local, nothing is stored.
 */
export default function ChatPanel() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (messages.length === 0 && !streaming) return;
    scrollRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages, streaming]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setStreaming(true);
    setMessages([...next, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: next }),
      });
      if (!res.ok || !res.body) throw new Error();
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        acc += dec.decode(value, { stream: true });
        setMessages(prev => {
          const copy = prev.slice();
          copy[copy.length - 1] = { role: "assistant", content: acc };
          return copy;
        });
      }
    } catch {
      setMessages(prev => {
        const copy = prev.slice();
        copy[copy.length - 1] = { role: "assistant", content: "I'm having trouble right now, try again in a moment." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="sbs-chat-panel">
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-sm" style={{ color: "#9ca3af" }} data-testid="sbs-chat-empty">
            Ask anything about FAFSA, your aid letter, or the document on the right.
            <div className="mt-3 grid gap-2">
              {[
                "Walk me through this 1040, what numbers go on the FAFSA?",
                "Which of these awards are grants vs. loans?",
                "What's the net cost on this letter?",
              ].map((s, i) => (
                <button key={i} onClick={() => setInput(s)}
                  className="text-left text-sm px-3 py-2 rounded-md border hover:bg-gray-50 transition-colors"
                  style={{ borderColor: "#e5e7eb", color: "#111928" }}
                  data-testid={`sbs-chat-suggest-${i}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
            {m.role === "user" ? (
              <div className="max-w-[85%] rounded-xl px-3 py-2 text-sm"
                style={{ background: "#eff6ff", border: "1px solid #bfdbfe", color: "#111928" }}>
                {m.content}
              </div>
            ) : (
              <div className="max-w-[90%] text-sm whitespace-pre-wrap" style={{ color: "#111928", lineHeight: 1.6 }}>
                {m.content || <span style={{ color: "#9ca3af" }}>Thinking…</span>}
              </div>
            )}
          </div>
        ))}
        <div ref={scrollRef} />
      </div>

      <div className="border-t p-3 flex gap-2" style={{ borderColor: "#e5e7eb", background: "#ffffff" }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
          placeholder="Ask about the document on the right…"
          disabled={streaming}
          className="flex-1 h-10 px-3 rounded-md text-sm outline-none"
          style={{ border: "1px solid #e5e7eb", background: "#ffffff", color: "#111928" }}
          data-testid="sbs-chat-input"
        />
        <button onClick={send} disabled={streaming || !input.trim()}
          className="h-10 w-10 rounded-md inline-flex items-center justify-center disabled:opacity-50"
          style={{ background: "#1d4ed8", color: "#ffffff" }}
          data-testid="sbs-chat-send"
          aria-label="Send">
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
